// Path: src/components/aols/aeo/OLSupdates/OLS2900/OLS2966.tsx
// fix: Replaced non-existent 'PackageOutlined' with 'ShoppingOutlined' to resolve module export error.
// 06/08/25 12:35 PM +07
import React from "react";
import { Layout, Typography, Breadcrumb } from "antd";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import styled from "styled-components";
// FIX: Replaced PackageOutlined with a valid icon, ShoppingOutlined.
import { HomeOutlined, ShoppingOutlined } from "@ant-design/icons";
import { InlineShareButtons } from "sharethis-reactjs";

const { Title, Paragraph } = Typography;

// --- Styled Components ---
const UpdateContainer = styled.div`
  background: #000000;
  min-height: 100vh;
  padding: 40px 20px;
  color: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const UpdateContent = styled.div`
  max-width: 800px;
  width: 100%;
  background: #1a1a1a;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
  text-align: center;
`;

const UpdateTitle = styled(Title)`
  && {
    color: #ffcc00 !important;
    margin-bottom: 30px;
  }
`;

const UpdateParagraph = styled(Paragraph)`
  && {
    color: #fff !important;
    font-size: 16px;
    line-height: 1.8;
    margin-bottom: 30px;
  }
`;

const HighlightedText = styled.span`
  font-weight: bold;
  color: #ffaa00;
`;

const FeatureHighlight = styled.span`
  font-size: 24px;
  font-weight: bold;
  background-color: #0427f4;
  color: #ffffff;
  padding: 5px 10px;
  border-radius: 8px;
`;

const UpdateImage = styled.img`
  max-width: 100%;
  border-radius: 8px;
  margin: 20px 0;
  border: 2px solid #ffaa00;
`;

const BreadcrumbWrapper = styled.div`
  width: 100%;
  max-width: 800px;
  margin: 0 auto 20px auto;
  text-align: left;

  .ant-breadcrumb a {
    color: #ffcc00;
    transition: color 0.3s ease;
  }
`;

const ShareThisWrapper = styled.div`
  margin-top: 30px;
  padding: 10px;
  background: #2a2a2a;
  border-radius: 8px;
`;

// --- Component Definition ---
const OLS2966: React.FC = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": "Discover Your Path: Announcing the New OLS Packages & Journeys Page",
    "description": "To enhance clarity and guide new users, OneLightSystem OLS has launched a new page detailing all recommended packages and paths to join our community.",
    "url": "https://www.olsme.com/olsupdates/ols2900/2966",
    "datePublished": "2025-06-08",
    "author": { "@type": "Organization", "name": "OneLightSystem OLS", "url": "https://www.olsme.com" },
    "publisher": { "@type": "Organization", "name": "OneLightSystem OLS", "logo": "https://www.olsme.com/logo.jpg" },
    "image": "https://images.placeholders.dev/?width=1200&height=630&text=OLS%20Packages%20&%20Journeys&bgColor=%23000000&textColor=%23ffaa00",
  };

  return (
    <Layout>
      <Helmet>
        <title>Discover Your Path: OLS Packages & Journeys | OLS Update</title>
        <meta
          name="description"
          content="Explore the new OLS Packages page to discover the best path for you. Compare OLS Student, OLSLM Assets, OLSME Subscription, and OLShealth program options."
        />
        <meta
          name="keywords"
          content="OLS Packages, OLS journeys, OLS Student, OLSLM, OLSME, OLShealth, join OLS, Sun Light Meditation"
        />
        <link rel="canonical" href="https://www.olsme.com/olsupdates/ols2900/2966" />
        <meta property="og:title" content="Discover Your Path: OLS Packages & Journeys | OLS Update" />
        <meta property="og:description" content="OneLightSystem OLS introduces a new page to clarify all available packages and help you choose your ideal path to enlightenment." />
        <meta property="og:url" content="https://www.olsme.com/olsupdates/ols2900/2966" />
        <meta property="og:type" content="article" />
        <meta property="og:image" content="https://images.placeholders.dev/?width=1200&height=630&text=OLS%20Packages%20&%20Journeys&bgColor=%23000000&textColor=%23ffaa00" />
        <meta property="og:image:alt" content="An announcement for the new OLS Packages and Journeys page." />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>

      <UpdateContainer>
        <BreadcrumbWrapper>
          <Breadcrumb
            items={[
              { title: <Link to="/"><HomeOutlined /> Home</Link> },
              { title: <Link to="/olsupdates">OLS Updates</Link> },
              { title: "OLS Packages Announcement" },
            ]}
          />
        </BreadcrumbWrapper>
        <UpdateContent>
          <UpdateTitle level={1}>
            {/* FIX: Replaced invalid icon with ShoppingOutlined */}
            <ShoppingOutlined style={{ marginRight: '15px' }} />
            Discover Your Path: Announcing OLS Packages & Journeys
          </UpdateTitle>
          <UpdateParagraph>
            To illuminate the way for new and existing members of our community, we have launched a dedicated <HighlightedText>Packages & Journeys</HighlightedText> page! As of <HighlightedText>June 8, 2025</HighlightedText>, you can now explore all recommended pathways into the OLS ecosystem in one convenient location.
          </UpdateParagraph>

          <UpdateImage
            src="https://images.placeholders.dev/?width=800&height=400&text=Find%20Your%20Path&bgColor=%231a1a1a&textColor=%23ffaa00"
            alt="A visual representation of the different OLS package options."
          />
          
          <UpdateParagraph>
            <strong>Find Your Ideal Journey:</strong><br />
            Our new page provides a clear, vertical comparison of the most common scenarios for joining OLS, including:
            <br />
            - The all-inclusive <HighlightedText>OLS Student</HighlightedText> path.
            <br />
            - The asset-focused <HighlightedText>OLSLM Holder</HighlightedText> journey.
            <br />
            - The accessible <HighlightedText>OLSME Subscription</HighlightedText> model.
            <br />
            - The wellness-centered <HighlightedText>OLShealth</HighlightedText> packages.
          </UpdateParagraph>
          <UpdateParagraph>
            <strong>Our Commitment to Clarity</strong><br />
            We created this page to empower you with the information needed to make a confident and informed decision about your spiritual and wellness journey. Our goal is to provide a transparent, easy-to-navigate map of the OLS universe, ensuring you find the path that resonates most deeply with you.
          </UpdateParagraph>
          <UpdateParagraph style={{ fontWeight: 'bold', color: '#ffcc00' }}>
            Ready to find your perfect OLS journey?
            <Link 
              to="/OLShealth/packages" 
              style={{ color: '#ffaa00', display: 'block', margin: '15px auto', textDecoration: 'underline', fontSize: '1.2em' }}
            >
              Explore All Packages Now!
            </Link>
          </UpdateParagraph>

          <ShareThisWrapper>
            <InlineShareButtons
              config={{
                alignment: "center",
                color: "social",
                enabled: true,
                networks: ["whatsapp", "linkedin", "messenger", "facebook", "twitter"],
                url: "https://www.olsme.com/olsupdates/ols2900/2966",
                image: "https://images.placeholders.dev/?width=1200&height=630&text=OLS%20Packages%20&%20Journeys&bgColor=%23000000&textColor=%23ffaa00",
                description: "OLS has launched a new page to clarify all available packages and help you choose your ideal path to enlightenment!",
                title: "Discover Your Path: OLS Packages & Journeys | OLS Update",
              }}
            />
          </ShareThisWrapper>
        </UpdateContent>
      </UpdateContainer>
    </Layout>
  );
};

export default OLS2966;